import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const environmentElements = [
  {
    id: 1,
    title: 'La luz',
    icon: 'ri-sun-line',
    science: 'La oscuridad es el disparador biológico más poderoso de la melatonina. Cuando la retina detecta la ausencia de luz azul (espectro 460-480 nm), envía una señal directa al núcleo supraquiasmático del hipotálamo, que a su vez ordena a la glándula pineal producir melatonina. El problema: la luz azul de celulares, tablets y TV suprime esta producción en hasta un 50%. No necesitás cortinas blackout de $200: una sábana oscura, una remera vieja, o simplemente apagar todas las pantallas 30 minutos antes del ritual de sueño, activa la cascada natural de melatonina.',
    practical: 'Apagá todas las pantallas 30 minutos antes de la rutina de sueño. Usá lo que tengas para oscurecer: una sábana oscura, una remera vieja sobre la lámpara, o simplemente girá la luz hacia la pared. La oscuridad no tiene que ser perfecta, solo consistente. El cerebro de tu bebé aprende por repetición.',
    neuroscience: 'Dr. Charles Czeisler — Harvard Medical School: "La luz artificial es el café de la noche para el cerebro. Una sola hora de exposición a luz azul retrasa la liberación de melatonina en 1.5 horas. En bebés, este efecto es aún más pronunciado porque su sistema circadiano aún no está maduro."',
  },
  {
    id: 2,
    title: 'La temperatura',
    icon: 'ri-thermometer-line',
    science: 'El cerebro del bebé necesita bajar su temperatura interna para iniciar el sueño. Entre 18 y 22 grados es el rango óptimo. Cuando la temperatura ambiente es muy alta, el cuerpo gasta energía regulando su calor en lugar de preparándose para el descanso. El Dr. Matthew Walker, en sus estudios en UC Berkeley, demostró que una temperatura ligeramente fría (pero nunca incómoda) aumenta el tiempo en sueño profundo y reduce los despertares nocturnos.',
    practical: 'No necesitás termómetro. Sentite la nuca de tu bebé: si está húmeda, tiene calor. Si está fría, tiene frío. Si está tibia y seca, está bien. Vestílo con una capa más que vos. Si tu bebé se despierta sudando, probá bajar la temperatura 2 grados. Si tiene frío, agregá una capa de algodón, no de fleece.',
    neuroscience: 'Dr. Matthew Walker — Center for Human Sleep Science, UC Berkeley: "El cerebro necesita bajar la temperatura corporal para transitar al sueño profundo. Un bebé que duerme con calor excesivo no solo despierta más, sino que su cerebro consolida menos la memoria durante la noche."',
  },
  {
    id: 3,
    title: 'El sonido',
    icon: 'ri-volume-up-line',
    science: 'El silencio absoluto no es lo ideal para un bebé. El ruido blanco imita el sonido del útero materno, que el bebé escuchó durante 9 meses a un volumen de aproximadamente 80 decibeles. En el útero, el bebé escuchaba constantemente el sonido del flujo sanguíneo, el latido materno, y los sonidos del tracto digestivo. El ruido blanco activa el reflejo de calmación, una respuesta neurobiológica que reduce el ritmo cardíaco y la tensión muscular.',
    practical: 'No necesitás un aparato de $100. Bajá una app gratuita de ruido blanco en tu celular. Empezá en volumen bajo y aumentá hasta que tu bebé se calme. El volumen ideal es similar al de una ducha a la distancia. Ponelo antes de empezar la rutina de sueño y dejalo durante toda la noche. Si se corta y el bebé despierta, es señal de que funciona.',
    neuroscience: 'Dr. Harvey Karp — Instituto de Desarrollo Infantil: "El ruido blanco no es un truco, es una herramienta neurobiológica. Imita el ambiente del útero que el bebé conoce como \'seguro\'. Esto activa el sistema nervioso parasimpático, la rama del sistema que calma, digiere y descansa."',
  },
  {
    id: 4,
    title: 'Tu presencia',
    icon: 'ri-heart-3-line',
    science: 'Este es el elemento que ninguna app puede reemplazar. Cuando vos estás calmada, tu sistema nervioso regula el de tu bebé a través de un mecanismo llamado co-regulación. El psiquiatra y neurocientífico Dr. Daniel Siegel explica que el sistema nervioso del bebé "lee" el estado de tu sistema nervioso a través de tu ritmo cardíaco, tu respiración, tu tono de voz y tu postura. Si tu corazón late a 90 pulsaciones, el bebé recibe la señal de alerta. Si tu corazón está en 60, recibe la señal de seguridad.',
    practical: 'No necesitás ser perfecta. Solo necesitás estar presente. Cuando tu bebé llora, tomá una respiración antes de responder. No porque estés fallando — porque estás transmitiendo calma. Si sentís que no podés calmarte, no hay vergüenza en pedir que alguien te reemplace 10 minutos. Tu bebé necesita tu calma, no tu presencia agotada.',
    neuroscience: 'Dr. Daniel Siegel — UCLA Mindful Awareness Research Center: "La co-regulación es el mecanismo más potente de regulación emocional infantil. El sistema nervioso del bebé no está diseñado para calmarse solo. Está diseñado para calmarse A TRAVÉS del sistema nervioso de un cuidador calmado."',
  },
];

const checklistItems = [
  { id: 1, category: 'La luz', text: 'Apagá todas las pantallas 30 minutos antes de la rutina de sueño' },
  { id: 2, category: 'La luz', text: 'Oscurecé la habitación con lo que tengas: sábana, remera, cortina' },
  { id: 3, category: 'La luz', text: 'Si usás luz nocturna, que sea roja o naranja, nunca blanca o azul' },
  { id: 4, category: 'La luz', text: 'Evitá encender la luz principal durante despertares nocturnos' },
  { id: 5, category: 'La temperatura', text: 'Sentí la nuca de tu bebé: húmeda = calor, fría = frío, tibia = bien' },
  { id: 6, category: 'La temperatura', text: 'Vestí a tu bebé con una capa más que lo que vos usás para dormir' },
  { id: 7, category: 'La temperatura', text: 'Preferí algodón sobre fleece o poliéster para la ropa de dormir' },
  { id: 8, category: 'La temperatura', text: 'Si hace calor, ventilá antes de dormir. Si hace frío, precalentá la habitación' },
  { id: 9, category: 'El sonido', text: 'Usá ruido blanco desde el celular (hay apps gratuitas)' },
  { id: 10, category: 'El sonido', text: 'El volumen debe ser similar al de una ducha a la distancia' },
  { id: 11, category: 'El sonido', text: 'Empezá el ruido blanco ANTES de la rutina de sueño' },
  { id: 12, category: 'El sonido', text: 'Dejalo durante toda la noche, no solo para dormir' },
  { id: 13, category: 'Tu presencia', text: 'Tomá una respiración antes de responder cuando llora' },
  { id: 14, category: 'Tu presencia', text: 'Pedí ayuda cuando sentís que no podés calmarte' },
  { id: 15, category: 'Tu presencia', text: 'Recordá: tu bebé siente tu ritmo cardíaco. Calmate primero vos.' },
];

export default function AmbienteIdealSueno() {
  const [activeElement, setActiveElement] = useState(0);
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleCheck = (id: number) => {
    setCheckedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const activeElementData = environmentElements[activeElement];

  return (
    <div className="min-h-screen bg-cream">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-sand/40">
        <div className="h-full bg-terracotta transition-all duration-150" style={{ width: `${scrollProgress}%` }} />
      </div>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 md:pt-28 pb-8 md:pb-14 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-center mb-6 md:mb-10">
            <div className="w-full lg:w-1/2">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-block bg-[#AAB7A3] text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('ambiente.tag1')}
                </span>
                <span className="inline-block bg-terracotta text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('ambiente.tag2')}
                </span>
                <span className="inline-block bg-sand text-[#3D2B1F] text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('common.basado_neurociencia')}
                </span>
              </div>
              <h1 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-[#8B6F5C] leading-tight mb-4">
                {t('ambiente.title1')} {t('ambiente.title2')}
              </h1>
              <p className="font-sans text-sm md:text-base text-[#3D2B1F] leading-relaxed">
                {t('ambiente.hero.desc')}
              </p>
            </div>
            <div className="w-full lg:w-1/2 flex items-center justify-center">
              <img
                src="https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/258f8ad4-5440-43ff-9aea-49b2c1fead9d_compressed_ChatGPT-Image-11-ago-2026-10_04_36-p.m..webp"
                alt="Mamá creando ambiente ideal de sueño para su bebé"
                className="w-full max-w-[270px] md:max-w-[336px] h-auto rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introducción Emocional */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-cream rounded-3xl p-8 md:p-12 border-l-4 border-terracotta">
            <p className="font-sans text-lg md:text-xl text-[#3D2B1F] leading-relaxed mb-6">
              Si estás leyendo esto, probablemente buscaste en Google a las 2 de la mañana qué temperatura tiene que tener la habitación, qué luz, qué ruido.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              Y probablemente sentiste que no tenías nada bien. Que el mundo te exigía una habitación perfecta que vos no podías costear, no podías armar, no sabías cómo hacer.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              <strong>El ambiente ideal no se compra. Se construye con presencia.</strong>
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              El elemento más poderoso para que tu bebé duerma no está en ninguna tienda. Está en vos.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed">
              Y eso, ya lo tenés.
            </p>
          </div>
        </div>
      </section>

      {/* Frase poética */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-6">
            <i className="ri-star-line text-cream text-2xl" />
          </div>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-4">
            "Tu bebé no necesita una habitación de revista. Necesita sentir que el mundo es predecible.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-4">
            Que cuando cierra los ojos, vos seguís ahí.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-4">
            Eso es el ambiente ideal.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed">
            Y eso, ya lo tenés."
          </p>
        </div>
      </section>

      {/* 4 Elementos del Ambiente Ideal con Tabs */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('ambiente.pilares_tag')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('ambiente.pilares_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('ambiente.pilares_desc')}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-8 md:mb-12 justify-center">
            {environmentElements.map((element, index) => (
              <button
                key={element.id}
                onClick={() => setActiveElement(index)}
                className={`inline-flex items-center gap-2 font-sans text-sm font-semibold px-5 py-3 rounded-full transition-all duration-300 whitespace-nowrap ${
                  activeElement === index
                    ? 'bg-terracotta text-white shadow-sm'
                    : 'bg-white text-[#3D2B1F] hover:bg-sand/50 border border-sand/40'
                }`}
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                  {element.id}
                </span>
                {element.title}
              </button>
            ))}
          </div>

          {/* Active Element Content */}
          <div className="bg-white rounded-3xl p-6 md:p-10 border border-sand/40">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#AAB7A3]/15 text-[#AAB7A3]">
                <i className={`${activeElementData.icon} text-xl`} />
              </div>
              <div>
                <span className="font-sans text-xs font-semibold text-terracotta uppercase tracking-wider">
                  Elemento {activeElementData.id} de 4
                </span>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#8B6F5C] leading-tight">
                  {activeElementData.title}
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8">
              <div className="bg-cream rounded-2xl p-6">
                <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-3">
                  {t('ambiente.la_ciencia')}
                </span>
                <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                  {activeElementData.science}
                </p>
              </div>
              <div className="bg-terracotta/10 rounded-2xl p-6 border border-terracotta/20">
                <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-wider mb-3">
                  {t('ambiente.que_hacer')}
                </span>
                <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed font-medium">
                  {activeElementData.practical}
                </p>
              </div>
            </div>

            {/* Cita del científico */}
            <div className="bg-[#4A3F35] rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-double-quotes-l text-cream text-xl" />
                </div>
                <div>
                  <p className="font-sans text-sm text-cream leading-relaxed mb-3">
                    {activeElementData.neuroscience.split('"')[1]}
                  </p>
                  <p className="font-sans text-xs text-cream/80">
                    {activeElementData.neuroscience.split('"')[2]}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation arrows */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-sand/30">
              <button
                onClick={() => setActiveElement((prev) => (prev > 0 ? prev - 1 : environmentElements.length - 1))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                <i className="ri-arrow-left-line" />
                {t('ambiente.elemento_anterior')}
              </button>
              <span className="font-sans text-xs text-[#5C4A3A]">
                {activeElement + 1} / {environmentElements.length}
              </span>
              <button
                onClick={() => setActiveElement((prev) => (prev < environmentElements.length - 1 ? prev + 1 : 0))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                {t('ambiente.elemento_siguiente')}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Checklist Descargable */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('ambiente.checklist_tag')}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-3">
              {t('ambiente.checklist_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F]">
              {t('ambiente.checklist_desc')}
            </p>
          </div>

          <div className="bg-cream rounded-3xl p-6 md:p-10 border border-sand/40">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="font-sans text-sm font-semibold text-[#3D2B1F]">
                  {t('ambiente.progreso')}
                </span>
                <span className="font-sans text-sm font-semibold text-terracotta">
                  {checkedItems.length} / {checklistItems.length}
                </span>
              </div>
              <div className="w-full h-2 bg-sand/40 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#AAB7A3] transition-all duration-300 rounded-full"
                  style={{ width: `${(checkedItems.length / checklistItems.length) * 100}%` }}
                />
              </div>
              {checkedItems.length === checklistItems.length && (
                <p className="font-sans text-sm text-[#AAB7A3] font-semibold mt-2">
                  {t('ambiente.completo')}
                </p>
              )}
            </div>

            {/* Categories */}
            {['La luz', 'La temperatura', 'El sonido', 'Tu presencia'].map((category) => (
              <div key={category} className="mb-6 last:mb-0">
                <h4 className="font-serif text-lg font-bold text-[#8B6F5C] mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-terracotta/15 text-terracotta text-xs font-bold">
                    {category === 'La luz' ? '1' : category === 'La temperatura' ? '2' : category === 'El sonido' ? '3' : '4'}
                  </span>
                  {category}
                </h4>
                <div className="space-y-2">
                  {checklistItems
                    .filter((item) => item.category === category)
                    .map((item) => (
                      <button
                        key={item.id}
                        onClick={() => toggleCheck(item.id)}
                        className={`w-full flex items-start gap-3 p-3 rounded-xl transition-all duration-200 text-left ${
                          checkedItems.includes(item.id)
                            ? 'bg-[#AAB7A3]/10 border border-[#AAB7A3]/30'
                            : 'bg-white border border-sand/30 hover:border-terracotta/20'
                        }`}
                      >
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all duration-200 ${
                          checkedItems.includes(item.id)
                            ? 'bg-[#AAB7A3] text-white'
                            : 'bg-sand/40 text-[#5C4A3A]'
                        }`}>
                          <i className={`${checkedItems.includes(item.id) ? 'ri-check-line' : 'ri-check-line'} text-xs`} />
                        </div>
                        <span className={`font-sans text-sm leading-relaxed ${checkedItems.includes(item.id) ? 'text-[#3D2B1F] line-through opacity-60' : 'text-[#3D2B1F]'}`}>
                          {item.text}
                        </span>
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cierre Emocional Profundo */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-6">
            <i className="ri-heart-3-line text-cream text-2xl" />
          </div>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-6">
            "Nadie te enseñó esto. Nadie te dijo que ibas a estar sola a las 3 de la mañana buscando respuestas en una pantalla fría mientras tu bebé llora.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-6">
            Ese desamparo es real. Ese miedo es real.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-6">
            Pero también es real esto: que estás acá, buscando, aprendiendo, queriendo hacerlo mejor.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-6">
            Y eso, que es invisible, que nadie ve, que no tiene foto ni like — eso es lo más grande que existe.
          </p>
          <p className="font-serif text-xl md:text-2xl text-white leading-relaxed mb-10">
            Lo esencial siempre fue invisible a los ojos. Y vos lo estás haciendo.
          </p>
          <div>
            <Link
              to="/fundamentos-sueno"
              className="inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-[#8B6F5C] transition-colors duration-300 whitespace-nowrap"
            >
              {t('common.conocer_programa')}
              <i className="ri-arrow-right-line ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA al Programa Completo */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-sand/40">
            <div className="text-center mb-8">
              <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-3">
                {t('tutorial.next_step')}
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-4">
                Esto es solo el principio
              </h2>
              <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
                El Programa Completo NeuroBabys incluye todo lo que necesitás para transformar el sueño de tu familia, paso a paso, con neurociencia y yoga mamá-bebé.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 text-[#AAB7A3] flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </div>
                <p className="font-sans text-sm text-[#3D2B1F]">
                  <strong>36 lecciones</strong> en video, organizadas en 6 módulos progresivos
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 text-[#AAB7A3] flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </div>
                <p className="font-sans text-sm text-[#3D2B1F]">
                  <strong>Secuencias de yoga mamá-bebé</strong> grabadas para hacer en casa
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 text-[#AAB7A3] flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </div>
                <p className="font-sans text-sm text-[#3D2B1F]">
                  <strong>Audio-guiadas</strong> de relajación progresiva de 5, 10 y 20 minutos
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 text-[#AAB7A3] flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </div>
                <p className="font-sans text-sm text-[#3D2B1F]">
                {t('tutorial.acceso_vida_bullet')}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 text-[#AAB7A3] flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </div>
                <p className="font-sans text-sm text-[#3D2B1F]">
                  <strong>Comunidad privada</strong> de mamás NeuroBabys con mentorías
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 text-[#AAB7A3] flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-xs" />
                </div>
                <p className="font-sans text-sm text-[#3D2B1F]">
                  <strong>Bebés 0 a 24 meses</strong>. Funciona para cada etapa de desarrollo.
                </p>
              </div>
            </div>

            <div className="text-center pt-6 border-t border-sand/30">
              <div className="mb-6">
                <span className="font-sans text-3xl md:text-4xl font-bold text-[#8B6F5C]">$147</span>
                <span className="font-sans text-sm text-[#5C4A3A] ml-1">USD</span>
                <span className="font-sans text-xs text-[#5C4A3A] ml-2">{t('tutorial.pago_unico')}</span>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/fundamentos-sueno"
                  className="inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-[#8B6F5C] transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
                >
                  {t('common.inscribirme_programa')}
                  <i className="ri-arrow-right-line ml-2" />
                </Link>
                <Link
                  to="/quiero-cambiar"
                  className="inline-flex items-center justify-center bg-white border-2 border-[#8B6F5C] text-[#8B6F5C] px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-[#8B6F5C] hover:text-white transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
                >
                  {t('common.ver_mas_gratis')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}